// AdminGroupLayoutEditor — full per-group config: available widgets,
// mandatory widgets, default rows, custom HTML widgets, allow_customization.
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import LayoutBuilder from "@/components/simulator/LayoutBuilder";
import { Plus, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGroupLayoutEditor({ groupId, groupName, getAuthHeaders, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [availableIds, setAvailableIds] = useState([]);
  const [mandatoryIds, setMandatoryIds] = useState([]);
  const [customWidgets, setCustomWidgets] = useState([]);
  const [defaultRows, setDefaultRows] = useState([]);
  const [defaultRowsByView, setDefaultRowsByView] = useState({});
  const [allowedViews, setAllowedViews] = useState(["outsourcing_contract", "outsourcing_green_receipts", "outsourcing_b2b", "perm"]);
  const [allowUserCustomization, setAllowUserCustomization] = useState(true);
  const [newCustomWidget, setNewCustomWidget] = useState({ name: "", html_content: "" });

  useEffect(() => {
    (async () => {
      try {
        const [catRes, cfgRes] = await Promise.all([
          axios.get(`${API}/widget-catalog`, { headers: getAuthHeaders() }),
          axios.get(`${API}/group-layout-config/admin/${groupId}`, { headers: getAuthHeaders() }),
        ]);
        setCatalog(catRes.data.catalog || []);
        setAvailableIds(cfgRes.data.available_widget_ids || catRes.data.catalog.map((w) => w.id));
        setMandatoryIds(cfgRes.data.mandatory_widget_ids || []);
        setCustomWidgets(cfgRes.data.custom_widgets || []);
        setDefaultRows(cfgRes.data.default_rows || []);
        setDefaultRowsByView(cfgRes.data.default_rows_by_view || {});
        setAllowedViews(cfgRes.data.allowed_views || ["outsourcing_contract", "outsourcing_green_receipts", "outsourcing_b2b", "perm"]);
        setAllowUserCustomization(cfgRes.data.allow_user_customization !== false);
      } catch (err) {
        toast.error("Erro ao carregar: " + (err?.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [groupId, getAuthHeaders]);

  const toggleAvailable = (id) => {
    if (availableIds.includes(id)) {
      setAvailableIds(availableIds.filter((x) => x !== id));
      setMandatoryIds(mandatoryIds.filter((x) => x !== id));
    } else {
      setAvailableIds([...availableIds, id]);
    }
  };
  const toggleMandatory = (id) => {
    if (mandatoryIds.includes(id)) setMandatoryIds(mandatoryIds.filter((x) => x !== id));
    else if (availableIds.includes(id)) setMandatoryIds([...mandatoryIds, id]);
  };

  const addCustomWidget = () => {
    if (!newCustomWidget.name.trim() || !newCustomWidget.html_content.trim()) {
      toast.error("Nome e conteúdo são obrigatórios");
      return;
    }
    const id = `custom_${Date.now()}`;
    const w = { id, name: newCustomWidget.name.trim(), html_content: newCustomWidget.html_content };
    setCustomWidgets([...customWidgets, w]);
    setAvailableIds([...availableIds, id]);
    setNewCustomWidget({ name: "", html_content: "" });
    toast.success("Widget custom adicionado");
  };
  const removeCustomWidget = (id) => {
    setCustomWidgets(customWidgets.filter((c) => c.id !== id));
    setAvailableIds(availableIds.filter((x) => x !== id));
    setMandatoryIds(mandatoryIds.filter((x) => x !== id));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/group-layout-config/admin`, {
        group_id: groupId,
        available_widget_ids: availableIds,
        mandatory_widget_ids: mandatoryIds,
        default_rows: defaultRows,
        default_rows_by_view: defaultRowsByView,
        allowed_views: allowedViews,
        custom_widgets: customWidgets,
        allow_user_customization: allowUserCustomization,
      }, { headers: getAuthHeaders() });
      toast.success(`Configuração do grupo "${groupName}" guardada`);
    } catch (err) {
      toast.error("Erro ao guardar: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-600" /></div>;

  const grouped = {};
  catalog.forEach((w) => { grouped[w.category] = grouped[w.category] || []; grouped[w.category].push(w); });

  return (
    <div className="space-y-6" data-testid="admin-group-layout-editor">
      <Card>
        <CardHeader><CardTitle className="text-base">1 · Widgets disponíveis para o grupo &quot;{groupName}&quot;</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(grouped).map(([cat, widgets]) => (
            <div key={cat}>
              <div className="text-xs uppercase font-semibold text-slate-500 mb-2">{cat === "form" ? "Formulário" : cat === "results" ? "Resultados" : "Ações"}</div>
              <div className="grid grid-cols-2 gap-2">
                {widgets.map((w) => {
                  const avail = availableIds.includes(w.id);
                  const mand = mandatoryIds.includes(w.id);
                  return (
                    <div key={w.id} className="flex items-start gap-2 p-2 border rounded hover:bg-slate-50">
                      <Checkbox checked={avail} onCheckedChange={() => toggleAvailable(w.id)} data-testid={`avail-${w.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{w.name}</div>
                        <div className="text-xs text-slate-500 truncate">{w.description}</div>
                        {avail && (
                          <label className="flex items-center gap-1 mt-1 text-xs text-amber-700">
                            <Checkbox checked={mand} onCheckedChange={() => toggleMandatory(w.id)} className="h-3 w-3" data-testid={`mand-${w.id}`} />
                            <span>Obrigatório (user não pode esconder)</span>
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">2 · Widgets Custom (blocos HTML criados por si)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {customWidgets.length > 0 && (
            <div className="space-y-2">
              {customWidgets.map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-2 p-2 border rounded bg-brand-50/50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-700">{c.name}</div>
                    <div className="text-xs text-slate-500 line-clamp-2">{c.html_content.replace(/<[^>]+>/g, "").slice(0, 120)}...</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeCustomWidget(c.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Nome</Label>
            <Input value={newCustomWidget.name} onChange={(e) => setNewCustomWidget({ ...newCustomWidget, name: e.target.value })} placeholder="Ex: Notas Comercial" data-testid="new-custom-name" />
            <Label className="text-xs">Conteúdo HTML</Label>
            <Textarea rows={4} value={newCustomWidget.html_content} onChange={(e) => setNewCustomWidget({ ...newCustomWidget, html_content: e.target.value })} placeholder='<p>Lembrete: verificar histórico do cliente antes de enviar proposta.</p>' data-testid="new-custom-html" />
            <Button variant="outline" size="sm" onClick={addCustomWidget} data-testid="add-custom-widget-btn"><Plus className="w-4 h-4 mr-1" /> Adicionar Widget Custom</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">3 · Vistas Permitidas para este Grupo</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">Escolha quais combinações Negócio×Regime este grupo pode usar. Utilizadores só verão estas vistas no simulador. (Admin vê sempre todas.)</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: "outsourcing_contract", label: "Outsourcing — Contrato" },
              { id: "outsourcing_green_receipts", label: "Outsourcing — Recibos Verdes" },
              { id: "outsourcing_b2b", label: "Outsourcing — B2B" },
              { id: "perm", label: "Perm" },
            ].map((v) => (
              <label key={v.id} className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 cursor-pointer" data-testid={`allowed-view-${v.id}-label`}>
                <Checkbox
                  checked={allowedViews.includes(v.id)}
                  onCheckedChange={() => {
                    setAllowedViews((prev) => prev.includes(v.id) ? prev.filter((x) => x !== v.id) : [...prev, v.id]);
                  }}
                  data-testid={`allowed-view-${v.id}`}
                />
                <span className="text-sm">{v.label}</span>
              </label>
            ))}
          </div>
          {allowedViews.length === 0 && <p className="text-xs text-red-600 mt-2">⚠️ Ao menos uma vista deve estar selecionada.</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">4 · Layout Default do Grupo</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">Este é o layout inicial que os utilizadores deste grupo verão. Podem personalizar por cima (se permitido abaixo). A alteração é guardada quando clicar em <strong>&quot;Guardar Configuração do Grupo&quot;</strong> em baixo.</p>
          <LayoutBuilder
            getAuthHeaders={getAuthHeaders}
            mode="admin"
            adminGroupId={groupId}
            initialRows={defaultRows}
            hideSaveButton
            onRowsChange={setDefaultRows}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">5 · Permissões</CardTitle></CardHeader>
        <CardContent>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="allow-customization-label">
            <Checkbox checked={allowUserCustomization} onCheckedChange={setAllowUserCustomization} data-testid="allow-customization-checkbox" />
            <span className="text-sm">Permitir que os utilizadores deste grupo personalizem o próprio layout</span>
          </label>
          {!allowUserCustomization && (
            <p className="text-xs text-amber-700 mt-2 ml-6">⚠️ Utilizadores ficam bloqueados no layout default deste grupo.</p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 sticky bottom-0 bg-white p-3 border-t">
        <Button variant="outline" onClick={onClose}>Fechar</Button>
        <Button onClick={save} disabled={saving} className="bg-brand-700 hover:bg-brand-800" data-testid="save-group-config-btn">
          {saving ? "A guardar…" : "Guardar Configuração do Grupo"}
        </Button>
      </div>
    </div>
  );
}
